import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const DrawCycleManager = () => {
  const [prizeStructure, setPrizeStructure] = useState([
    { position: '1st', number: '', points: '' }
  ]);
  const [message, setMessage] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [existingPrizes, setExistingPrizes] = useState(null);
  const [todaysBets, setTodaysBets] = useState({ total_bets: 0, unique_numbers: 0 });

  useEffect(() => {
    loadTodaysData();
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const loadTodaysData = async () => {
    try {
      // Load existing prize structure for today
      const drawResponse = await axios.get(`${API_BASE_URL}/telegram/draw/today`);
      
      if (drawResponse.data && drawResponse.data.prize_allocations) {
        setExistingPrizes(drawResponse.data);
        // Convert existing prizes to form format
        const existingStructure = Object.entries(drawResponse.data.prize_allocations).map(([number, points], index) => ({
          position: ['1st', '2nd', '3rd', '4th', '5th'][index] || `${index + 1}th`,
          number,
          points: points.toString()
        }));
        setPrizeStructure(existingStructure);
      }

      // Load today's bets summary
      const betsResponse = await axios.get(`${API_BASE_URL}/telegram/bets/today`);
      if (betsResponse.data) {
        setTodaysBets({
          total_bets: betsResponse.data.total_bets || 0,
          unique_numbers: Object.keys(betsResponse.data.grouped_bets || {}).length
        });
      }
    } catch (err) {
      console.error('Error loading today\'s data:', err);
    }
  };

  const getTimeUntilDeadline = () => {
    const now = new Date();
    const today = new Date();
    today.setHours(21, 0, 0, 0); // 9 PM today
    
    if (now > today) {
      return { expired: true, text: 'Deadline Passed' };
    }
    
    const diff = today - now;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return { 
      expired: false, 
      text: `${hours}h ${minutes}m remaining`,
      hours,
      minutes
    };
  };

  const canModifyPrizes = () => {
    const timeLeft = getTimeUntilDeadline();
    return !timeLeft.expired;
  };

  const handleSave = async () => {
    if (!canModifyPrizes()) {
      setMessage('❌ Cannot modify prizes after 9 PM deadline');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    // Validate prize structure
    const validPrizes = prizeStructure.filter(p => p.number && p.points);
    if (validPrizes.length === 0) {
      setMessage('❌ Please add at least one prize with lucky number and points');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const winningNumbers = validPrizes.map(p => p.number);
      const prizeAllocations = {};
      validPrizes.forEach(p => {
        prizeAllocations[p.number] = parseInt(p.points);
      });

      await axios.post(`${API_BASE_URL}/telegram/draw/finalize`, {
        winning_numbers: winningNumbers,
        prize_allocations: prizeAllocations
      });
      
      setMessage('✅ Prize structure saved! Winners will be notified at 9 PM.');
      setExistingPrizes({ winning_numbers: winningNumbers, prize_allocations: prizeAllocations });
      setTimeout(() => setMessage(''), 5000);
    } catch (err) {
      setMessage('❌ Failed to save prize structure');
      setTimeout(() => setMessage(''), 3000);
    }
  };

  const addPosition = () => {
    const positions = ['1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', '11th', '12th', '13th', '14th', '15th', '16th', '17th', '18th', '19th', '20th', '21st', '22nd', '23rd'];
    const nextPosition = positions[prizeStructure.length] || `${prizeStructure.length + 1}th`;
    setPrizeStructure([...prizeStructure, { position: nextPosition, number: '', points: '' }]);
  };

  const removePosition = (index) => {
    if (prizeStructure.length > 1) {
      setPrizeStructure(prizeStructure.filter((_, i) => i !== index));
    }
  };

  const updatePrize = (index, field, value) => {
    const newStructure = [...prizeStructure];
    newStructure[index][field] = value;
    setPrizeStructure(newStructure);
  };

  const timeLeft = getTimeUntilDeadline();
  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">🏆 Today's Lucky Draw - {today}</h2>
      
      {message && (
        <div className={`mb-4 p-3 rounded ${message.includes('✅') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {message}
        </div>
      )}

      {/* Status Bar */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-blue-600">{todaysBets.total_bets}</div>
            <div className="text-sm text-gray-600">Total Bets</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{todaysBets.unique_numbers}</div>
            <div className="text-sm text-gray-600">Unique Numbers</div>
          </div>
          <div className="text-center">
            <div className={`text-2xl font-bold ${timeLeft.expired ? 'text-red-600' : 'text-orange-600'}`}>
              {timeLeft.text}
            </div>
            <div className="text-sm text-gray-600">Until 9 PM Deadline</div>
          </div>
          <div className="text-center">
            <div className="text-lg font-bold text-gray-700">
              {currentTime.toLocaleTimeString()}
            </div>
            <div className="text-sm text-gray-600">Malaysian Time</div>
          </div>
        </div>
      </div>

      {/* Existing Prizes Display */}
      {existingPrizes && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-semibold text-green-800 mb-2">✅ Prize Structure Already Set</h3>
          <div className="text-sm text-green-700">
            <strong>Note:</strong> You can modify the prize structure until 9 PM. After that, winners will be automatically notified.
          </div>
          <div className="mt-2">
            {Object.entries(existingPrizes.prize_allocations || {}).map(([number, points], index) => (
              <span key={number} className="inline-block bg-green-100 text-green-800 px-2 py-1 rounded mr-2 mb-1">
                {number} → {points} points
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Prize Structure Form */}
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">🏆 Prize Structure Configuration</h3>
        
        {!canModifyPrizes() && (
          <div className="bg-red-50 border border-red-200 rounded p-3 mb-4">
            <div className="text-red-800 font-medium">⏰ Deadline Passed</div>
            <div className="text-red-600 text-sm">Prize structure cannot be modified after 9 PM Malaysian Time.</div>
          </div>
        )}
        
        <div className="space-y-3">
          {prizeStructure.map((prize, index) => (
            <div key={index} className="flex items-center gap-3 p-3 border rounded">
              <span className="font-medium w-12 text-center">{prize.position}</span>
              <div className="flex-1">
                <input 
                  type="text"
                  placeholder="Lucky Number (e.g., 1234)"
                  className="w-full border rounded px-3 py-2"
                  value={prize.number}
                  onChange={(e) => updatePrize(index, 'number', e.target.value)}
                  disabled={!canModifyPrizes()}
                />
              </div>
              <div className="w-32">
                <input 
                  type="number"
                  placeholder="Points"
                  className="w-full border rounded px-3 py-2"
                  value={prize.points}
                  onChange={(e) => updatePrize(index, 'points', e.target.value)}
                  disabled={!canModifyPrizes()}
                />
              </div>
              {prizeStructure.length > 1 && canModifyPrizes() && (
                <button 
                  onClick={() => removePosition(index)}
                  className="text-red-500 hover:text-red-700 px-2 py-1"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
        </div>

        {canModifyPrizes() && prizeStructure.length < 23 && (
          <button 
            onClick={addPosition}
            className="mt-3 text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            + Add Position
          </button>
        )}
      </div>

      {/* Save Button */}
      <div className="mt-6">
        <button 
          onClick={handleSave}
          disabled={!canModifyPrizes()}
          className={`px-6 py-3 rounded font-medium ${
            canModifyPrizes() 
              ? 'bg-blue-600 text-white hover:bg-blue-700' 
              : 'bg-gray-300 text-gray-500 cursor-not-allowed'
          }`}
        >
          {existingPrizes ? 'Update Prize Structure' : 'Save Prize Structure'}
        </button>
      </div>

      {/* Warning */}
      <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded">
        <div className="text-yellow-800 font-medium">⚠️ Important Notes:</div>
        <ul className="text-yellow-700 text-sm mt-1 list-disc list-inside">
          <li>Prize structure can only be set once per day and modified until 9 PM Malaysian Time</li>
          <li>Winners will be automatically notified at 9 PM based on your configuration</li>
          <li>Make sure to save your changes before the deadline</li>
        </ul>
      </div>
    </div>
  );
};

export default DrawCycleManager;